import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

const DistrictDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [districtData, setDistrictData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { notification, showError, hideNotification } = useNotification();

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/districts/${id}/details`);
      setDistrictData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch district details:', error);
      showError('Failed to load district details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading district details...</p>
        </div>
      </div>
    );
  }

  if (!districtData || !districtData.district) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">District not found</p>
        <button
          onClick={() => navigate('/districts')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark"
        >
          Back to Districts
        </button>
      </div>
    );
  }

  const { district, schools, stats } = districtData;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'schools', label: 'Schools' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/districts')}
            className="text-primary hover:text-primaryDark mb-2 flex items-center"
          >
            ← Back to Districts
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{district.name}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Schools</p>
                <p className="text-xl font-bold text-gray-800">{stats.totalSchools}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-xl font-bold text-gray-800">{stats.totalUsers}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-xl font-bold text-gray-800">{stats.activeUsers}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Pending Users</p>
                <p className="text-xl font-bold text-gray-800">{stats.pendingUsers}</p>
              </div>
            </div>

            <div className="mt-6">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20 shadow-sm">
                <p className="text-sm text-gray-700 font-medium mb-2">Completion Rate</p>
                <p className="text-4xl font-bold text-primary">{stats.completionRate}%</p>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {stats.completedEnrollments} completed out of {stats.totalEnrollments} enrollments
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schools' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Schools ({schools.length})</h3>
            {schools.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No schools in this district</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {schools.map((school) => (
                  <div
                    key={school.id}
                    onClick={() => navigate(`/schools/${school.id}`)}
                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{school.name}</h4>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Email:</span> {school.contactEmail}
                    </p>
                    {school.contactPhone && (
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Phone:</span> {school.contactPhone}
                      </p>
                    )}
                    {school.address && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Address:</span> {school.address}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Licenses:</span>{' '}
                      {school.licensesUsed} / {school.licenseCount}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default DistrictDetail;


